import { useEffect, useState, Fragment, memo, type ReactNode, type SyntheticEvent } from 'react';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';
import type { LocaleString } from '@web-chat-core/ui-kit';
import { Typography } from '@web-chat-core/ui-kit';
import { AnchoredPopover, type AnchoredPopoverProps } from './components/AnchoredPopover';
import { OnboardingBtn, OnboardingTitle, OnboardingWrapper } from './view';

interface StepType {
    target: string;
    titlei18nKey: string;
    descriptioni18nKey: string;
    confirmi18nKey?: string;
    align?: 'start' | 'center' | 'end';
    maxWidth?: number | string;
}

interface OnboardingProps {
    steps: StepType[];
    isShowOnce?: boolean;
    storageKey?: string;
    withSearchTopLevelZIndex?: AnchoredPopoverProps['withSearchTopLevelZIndex'];
    withArrow?: AnchoredPopoverProps['withArrow'];
    sideOffset?: AnchoredPopoverProps['sideOffset'];
    /**
     * Не переносить фокус в подсказку при открытии.
     * Нужно онбордингу у поля ввода: подсказка монтируется заново, когда поле пустеет,
     * и иначе забирает курсор после отправки или стирания текста.
     * @default false
     */
    preventAutoFocus?: boolean;
    onEndAction?: () => void;
}

// eslint-disable-next-line no-control-regex
const LINE_BREAK_RE = /\r\n|[\n\r\u0085\u000B\u000C\u2028\u2029]/;

const Onboarding = memo(
    ({
        steps,
        storageKey,
        isShowOnce = true,
        withSearchTopLevelZIndex,
        withArrow,
        sideOffset,
        preventAutoFocus = false,
        onEndAction = noop,
    }: OnboardingProps) => {
        const { t } = useTranslation();
        const [step, setStep] = useState(0);
        const handleNext = (e?: SyntheticEvent) => {
            e?.stopPropagation();
            setStep(step + 1);
        };
        const {
            target,
            titlei18nKey,
            descriptioni18nKey,
            confirmi18nKey = 'web_button_got_it',
            align,
            maxWidth,
        } = steps[step] || {};
        const title = titlei18nKey ? t(titlei18nKey as LocaleString) : '';
        const raw = descriptioni18nKey ? t(descriptioni18nKey as LocaleString) : '';

        const description: string | ReactNode[] = LINE_BREAK_RE.test(raw)
            ? raw.split(LINE_BREAK_RE).map((text, index) => (
                  <Fragment key={index}>
                      {text}
                      <br />
                  </Fragment>
              ))
            : raw;
        const confirmText = confirmi18nKey ? t(confirmi18nKey as LocaleString) : '';
        const storage = isShowOnce ? localStorage : sessionStorage;

        useEffect(() => {
            if (steps.length > 0 && step === steps.length) {
                onEndAction();
                storageKey && storage.setItem(storageKey, 'true');
            }
        }, [onEndAction, step, steps.length, storage, storageKey]);

        if (steps.length < 1 || step === steps.length || (storageKey && storage.getItem(storageKey))) {
            return null;
        }

        return (
            <AnchoredPopover
                selector={target ?? ''}
                open={step < steps.length}
                align={align}
                maxWidth={maxWidth}
                withSearchTopLevelZIndex={withSearchTopLevelZIndex}
                withArrow={withArrow}
                sideOffset={sideOffset}
                preventAutoFocus={preventAutoFocus}
                next={handleNext}
            >
                <OnboardingWrapper>
                    <OnboardingTitle
                        variant="title9"
                        color="inherit"
                    >
                        {title}
                    </OnboardingTitle>
                    <Typography
                        variant="body3"
                        color="inherit"
                        margin="0 0 4px 0"
                    >
                        {description}
                    </Typography>
                    <OnboardingBtn
                        variant="primaryWhite"
                        onClick={handleNext}
                        size="medium"
                    >
                        {confirmText}
                    </OnboardingBtn>
                </OnboardingWrapper>
            </AnchoredPopover>
        );
    },
);

Onboarding.displayName = 'Onboarding';

export { Onboarding };
